import { z } from 'zod';
import { assertCapacity } from '../infrastructure/backup/snapshot';
import { NookDatabase, assertWritable, markSearchDirty } from '../infrastructure/database/database';
import type { Capture, Draft, SaveDraftInput } from '../domain/capture/model';
import { hasContent } from '../domain/capture/model';
import { NookError } from '../domain/shared/errors';

const saveSchema = z.object({
  operationId: z.uuid(), draftId: z.uuid(), captureId: z.uuid(),
  baseRevision: z.number().int().nonnegative(), expectedDraftRevision: z.number().int().nonnegative(),
  body: z.string().max(200_000), attachmentIds: z.array(z.uuid()).max(30),
});
async function fingerprint(value: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  const hash = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
export type PublishResult = { kind: 'saved'; capture: Capture } | { kind: 'conflict'; draft: Draft };

export class CaptureService {
  constructor(public readonly db: NookDatabase) {}

  async saveDraft(input: SaveDraftInput): Promise<Draft> {
    const validated = saveSchema.safeParse(input);
    if (!validated.success) throw new NookError('validation', '内容格式不正确或过长，尚未保存。请先导出文本。');
    if (new Set(input.attachmentIds).size !== input.attachmentIds.length) throw new NookError('validation', '图片引用重复，尚未保存。');
    const hash = await fingerprint(validated.data);
    return this.db.transaction('rw', this.db.tables, async () => {
      await assertWritable(this.db);
      const done = await this.db.operations.get(input.operationId);
      if (done) {
        if (done.kind !== 'save-draft' || done.fingerprint !== hash) throw new NookError('operation', '这次操作的内容发生变化，请重新保存。');
        const draft = await this.db.drafts.get(done.resultId);
        if (!draft || draft.revision !== done.resultRevision) throw new NookError('conflict', '这份草稿已在另一处变化，输入仍保留，请重新打开草稿或导出文本。');
        return draft;
      }
      const now = new Date().toISOString();
      const previous = await this.db.drafts.get(input.draftId);
      const capture = await this.db.captures.get(input.captureId);
      if (previous && previous.captureId !== input.captureId) throw new NookError('validation', '草稿归属不匹配。');
      const fork = !!previous?.deletedAt || (previous?.revision ?? 0) !== input.expectedDraftRevision;
      const staleBase = !!capture?.deletedAt || (capture?.revision ?? 0) !== input.baseRevision;
      const targetId = fork ? crypto.randomUUID() : input.draftId;

      for (const prepared of input.prepared ?? []) {
        if (!input.attachmentIds.includes(prepared.metadata.id)) continue;
        const { metadata, data } = prepared;
        if (metadata.sha256 !== data.sha256 || metadata.bytes !== data.blob.size || data.bytes !== metadata.bytes) throw new NookError('attachment', '图片数据不完整，文字仍在编辑器中。');
        const existing = await this.db.attachments.get(metadata.id);
        if (existing && existing.sha256 !== metadata.sha256) throw new NookError('attachment', '图片标识冲突，未覆盖原图片。');
        await this.db.attachments.put(metadata);
        if (!(await this.db.attachmentBlobs.get(data.sha256))) await this.db.attachmentBlobs.add(data);
      }
      for (const id of input.attachmentIds) {
        const attachment = await this.db.attachments.get(id);
        if (!attachment || !(await this.db.attachmentBlobs.get(attachment.sha256))) throw new NookError('attachment', '有图片尚未保存在本机，请重试或移除该图片后保存文字。');
      }
      const draft: Draft = {
        id: targetId, captureId: input.captureId, baseRevision: input.baseRevision,
        body: input.body, attachmentIds: input.attachmentIds,
        revision: fork ? 1 : (previous?.revision ?? 0) + 1,
        state: fork || staleBase || previous?.state === 'conflict' ? 'conflict' : 'active',
        createdAt: previous?.createdAt ?? now, updatedAt: now,
      };
      await this.db.drafts.put(draft);
      await this.db.operations.add({ id: input.operationId, kind: 'save-draft', fingerprint: hash, resultId: draft.id, resultRevision: draft.revision, committedAt: now });
      await markSearchDirty(this.db);await assertCapacity(this.db);
      return draft;
    });
  }

  async publish(draftId: string, expectedRevision: number, operationId: string, asCopy = false): Promise<PublishResult> {
    const hash = await fingerprint({ draftId, expectedRevision, asCopy });
    return this.db.transaction('rw', this.db.tables, async () => {
      await assertWritable(this.db);
      const done = await this.db.operations.get(operationId);
      if (done) {
        if (done.kind !== 'publish' || done.fingerprint !== hash) throw new NookError('operation', '操作标识已用于其他内容。');
        const capture = await this.db.captures.get(done.resultId);
        if (!capture) throw new NookError('missing', '原提交记录无法读取，未重复创建。');
        return { kind: 'saved', capture };
      }
      const draft = await this.db.drafts.get(draftId);
      if (!draft || draft.deletedAt) throw new NookError('missing', '草稿无法读取，未创建重复记录。');
      if (draft.revision !== expectedRevision) throw new NookError('conflict', '草稿已在另一处变化，请先保留输入并检查草稿。');
      if (!hasContent(draft.body, draft.attachmentIds)) throw new NookError('validation', '写一点内容，或添加一张图片后再存下。');
      const previous = await this.db.captures.get(draft.captureId);
      if (!asCopy && (!!previous?.deletedAt || draft.state === 'conflict' || (previous?.revision ?? 0) !== draft.baseRevision)) {
        const conflict = { ...draft, state: 'conflict' as const };
        await this.db.drafts.put(conflict);
        return { kind: 'conflict', draft: conflict };
      }
      const now = new Date().toISOString();
      const capture: Capture = {
        id: asCopy ? crypto.randomUUID() : draft.captureId,
        body: draft.body, attachmentIds: draft.attachmentIds, state: previous?.state ?? 'inbox',
        revision: asCopy ? 1 : (previous?.revision ?? 0) + 1,
        createdAt: asCopy ? now : (previous?.createdAt ?? draft.createdAt), updatedAt: now,
        source: 'user', archivedAt: null, deletedAt: null,
      };
      if(!previous&&!asCopy)for(const table of [this.db.tasks,this.db.events,this.db.projects,this.db.resources,this.db.decisions,this.db.diaryEntries,this.db.sopTemplates,this.db.sopRuns,this.db.manualStudyPlans,this.db.manualStudySessions])if(await table.get(capture.id))throw new NookError('validation','稳定 ID 已被另一对象使用。');
      await this.db.captures.put(capture);
      await this.db.revisions.add({ id: `${capture.id}:${capture.revision}`, captureId: capture.id, revision: capture.revision, reason: previous && !asCopy ? 'edited' : 'created', recordedAt: now, snapshot: capture });
      await this.db.drafts.delete(draftId);
      await this.db.operations.add({ id: operationId, kind: 'publish', fingerprint: hash, resultId: capture.id, resultRevision: capture.revision, committedAt: now });
      await markSearchDirty(this.db);await assertCapacity(this.db);
      return { kind: 'saved', capture };
    });
  }

  async discardDraft(draftId: string, expectedRevision: number, operationId: string): Promise<void> {
    const hash = await fingerprint({ draftId, expectedRevision });
    await this.db.transaction('rw', this.db.tables, async () => {
      await assertWritable(this.db);
      const done = await this.db.operations.get(operationId);
      if (done) {
        if (done.kind !== 'discard-draft' || done.fingerprint !== hash) throw new NookError('operation', '操作标识已被使用。');
        return;
      }
      const draft = await this.db.drafts.get(draftId);
      if (draft && draft.revision !== expectedRevision) throw new NookError('conflict', '草稿已变化，未丢弃另一处的内容。');
      if(draft) await this.db.drafts.put({ ...draft, revision: draft.revision+1, updatedAt: new Date().toISOString(), deletedAt: new Date().toISOString() });
      await this.db.operations.add({ id: operationId, kind: 'discard-draft', fingerprint: hash, resultId: draftId, resultRevision: expectedRevision, committedAt: new Date().toISOString() });
    });
  }
}
